import React from "react"
import { Icon } from "../../../components"
import useCarrousel from "../../../hooks/useCarrousel"
import { numberAbreviation } from "../../../lib/format"
import * as M from "../../Model/rentalsModel"
import * as A from "fp-ts/Array"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/lib/function"
import dayjs from "dayjs"
import { isBefore, isNext, isSame, sameOrAfter } from "../../../lib/calendar"

export default function ({
  items,
  onSelect,
}: {
  items: M.BookingIncome["breakdown"]
  onSelect: (index: number, id: string) => void
}): JSX.Element {
  const container = React.useRef<HTMLDivElement>(null)
  const prevButton = React.useRef<HTMLButtonElement>(null)
  const nextButton = React.useRef<HTMLButtonElement>(null)

  const { scrollContainerBy, scrollToIndex } = useCarrousel({
    containerRef: container,
    prevButtonRef: prevButton,
    nextButtonRef: nextButton,
  })

  React.useEffect(() => {
    return pipe(
      items,
      A.findIndex(item => sameOrAfter(new Date(item.range.start), "month")),
      O.match(() => scrollToIndex(items.length - 1), scrollToIndex)
    )
  })

  return (
    <div data-cy="dashboard-rentals-breakdown">
      <div
        className="flex items-center gap-2 mb-6"
        data-cy={`dashboard-rentals-breakdown-carousel`}
      >
        <h4
          className="heading flex-1"
          data-cy={`dashboard-rentals-breakdown-carousel-title`}
        >
          Monthly breakdown
        </h4>
        {items.length > 1 && (
          <>
            <button
              ref={prevButton}
              data-cy={`dashboard-rentals-breakdown-carousel-left`}
              className="button-icon hidden md:block rotate-180 disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("left")}
              aria-label="scroll carousel left"
            >
              <Icon.ChevronRight />
            </button>
            <button
              ref={nextButton}
              data-cy={`dashboard-rentals-breakdown-carousel-right`}
              className=" button-icon hidden md:block disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("right")}
              aria-label="scroll carousel right"
            >
              <Icon.ChevronRight />
            </button>
          </>
        )}
      </div>
      <div ref={container} className="carousel">
        {items.map((item, idx) => {
          const { name, value } = numberAbreviation(Number(item.totalIncome))

          return (
            <button
              id={`month-${idx}-btn`}
              key={`month-${idx}`}
              aria-label="Open detailed view"
              onClick={() => onSelect(idx, `month-${idx}-btn`)}
              data-cy={`dashboard-rental-breakdown-card-${idx}`}
            >
              <div
                className={
                  "card p-[25px] w-[210px] h-[177px] flex-[0_0_auto] bg-slate focus-visible:outline-brick text-white text-left flex flex-col"
                }
              >
                <h5 className="heading text-white text-[3.2rem] leading-[0.9] leading tracking-[0.03em]">
                  <span className="text-[0.8em]">£ {value}</span>
                  <span className=" text-[0.8em]">
                    {O.getOrElse(() => "")(name)}
                  </span>
                </h5>
                <p className=" mt-[18px] text-md text-cutoff">
                  {isNext(new Date(item.range.start), "year")
                    ? dayjs(item.range.start).format("MMMM YYYY")
                    : dayjs(item.range.start).format("MMMM")}
                </p>
                {isBefore(new Date(item.range.start), "month") && (
                  <p
                    className="mt-[18px] tag px-2 glass"
                    data-cy={`dashboard-rental-breakdown-card-${idx}-past`}
                  >
                    PAST
                  </p>
                )}
                {isSame(new Date(item.range.start), "month") && (
                  <p
                    className="mt-[18px] tag px-2 green"
                    data-cy={`dashboard-rental-breakdown-card-${idx}-current`}
                  >
                    CURRENT
                  </p>
                )}
                {isNext(new Date(item.range.start), "month") && (
                  <p
                    className="mt-[18px] tag px-2 yellow"
                    data-cy={`dashboard-rental-breakdown-card-${idx}-forward`}
                  >
                    FORWARD
                  </p>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
